import {
  ContentNode,
  ContentNodeUtils,
  DataTypeLocator,
  RuntimeOperations,
} from '@helo/cms'
import { Assert, If, flow } from '@helo/utils'

/**
 * Enriched table column config as stored on AG Grid colDef.context.
 * Prefer `columnConfig.state` (author overrides) over top-level `state`.
 * Never apply enriched DataTypeConfig `config.state` — that can clobber
 * already-resolved parent-REFERENCE asText.
 */
export type TableClipboardColumnConfig = {
  selector: DataTypeLocator
  state?: Record<string, unknown>
  columnConfig?: {
    state?: Record<string, unknown>
  }
}

/**
 * Format one table column cell as text from the row ContentNode.
 * Applies column state overrides + optional runtime ops, then asText with
 * no call-site date override (table display formatting wins). Numbers use
 * `raw` so clipboard copy has no grouping separators.
 */
export const columnFieldAsText =
  (columnConfig?: TableClipboardColumnConfig | null) =>
  (row: ContentNode | null | undefined): string => {
    if (!columnConfig?.selector || !row) {
      return '-'
    }

    const columnState =
      columnConfig.columnConfig?.state ?? columnConfig.state ?? {}
    const hasColumnState = Object.keys(columnState).length > 0

    return flow(
      ContentNodeUtils.findContentNode(columnConfig.selector),
      Assert.orReturn(ContentNodeUtils.isKind('FIELD'), () => '-'),
      If.then(
        () => hasColumnState,
        flow(
          ContentNodeUtils.setState([{ state: columnState }] as any),
          RuntimeOperations.processContentNode(),
        ),
      ),
      ContentNodeUtils.asText({
        numberFormat: 'raw',
      }),
    )(row)
  }
